import { useState, useEffect } from 'react';
import { Search, Bell, RefreshCw, AlertCircle } from 'lucide-react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { SyncEvent } from '../../types/content';
import { MetricCard, Badge, EmptyState } from '../shared';

export function SyncLog() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [syncEvents, setSyncEvents] = useState<SyncEvent[]>([]);

  useEffect(() => {
    loadSyncEvents();
  }, [user]);

  const loadSyncEvents = async () => {
    if (!user) return;

    try {
      const q = query(
        collection(db, 'syncEvents'),
        where('userId', '==', user.uid),
        orderBy('startedAt', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      setSyncEvents(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }) as SyncEvent));
    } catch (error) {
      console.error('Error loading sync events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats for today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayEvents = syncEvents.filter(e => e.startedAt.toDate() >= today);

  const syncsToday = todayEvents.length;
  const recordsSynced = todayEvents.reduce((sum, e) => sum + e.recordsAdded + e.recordsUpdated, 0);
  const errorCount = todayEvents.reduce((sum, e) => sum + e.errorsCount, 0);
  const avgTime = todayEvents.length > 0
    ? Math.round(todayEvents.reduce((sum, e) => sum + e.duration, 0) / todayEvents.length / 1000)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t2-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Content</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Sync Log</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search syncs..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t2-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold text-t2-primary uppercase tracking-wide mb-1">Kodiac Content</p>
            <h1 className="text-3xl font-semibold text-gray-900">Sync Log</h1>
            <p className="text-sm text-gray-600 mt-1">Monitor content synchronization activity and performance</p>
          </div>

          <button
            onClick={loadSyncEvents}
            className="flex items-center gap-2 px-4 py-2 bg-t2-primary text-white rounded-lg hover:bg-t2-dark"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* 4-Stat Row */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <MetricCard
            label="Syncs Today"
            value={syncsToday.toString()}
          />
          <MetricCard
            label="Records Synced"
            value={recordsSynced.toString()}
          />
          <MetricCard
            label="Errors"
            value={errorCount.toString()}
          />
          <MetricCard
            label="Avg Time"
            value={`${avgTime}s`}
          />
        </div>

        {/* Recent Sync Events Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">Recent Sync Events</h2>
          </div>

          {syncEvents.length === 0 ? (
            <div className="p-12">
              <EmptyState
                icon={RefreshCw}
                title="No sync events yet"
                description="Sync events will appear here as connectors sync content"
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-warm border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Connector
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Started
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Duration
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Added
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Updated
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Deleted
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Errors
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {syncEvents.map(event => {
                    const duration = Math.round(event.duration / 1000);

                    return (
                      <tr key={event.id} className="hover:bg-surface-warm transition-colors">
                        <td className="px-4 py-3">
                          <div>
                            <Badge variant="primary">{event.connectorType}</Badge>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant={
                              event.status === 'success'
                                ? 'success'
                                : event.status === 'failed'
                                ? 'critical'
                                : 'warning'
                            }
                          >
                            {event.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900">
                            {event.startedAt.toDate().toLocaleDateString()}
                          </div>
                          <div className="text-xs text-gray-600">
                            {event.startedAt.toDate().toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900">{duration}s</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-success-green">
                            +{event.recordsAdded}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-blue-400">
                            ~{event.recordsUpdated}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-600">
                            {event.recordsDeleted > 0 ? `-${event.recordsDeleted}` : '0'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {event.errorsCount > 0 ? (
                            <div className="flex items-center gap-1">
                              <AlertCircle className="w-4 h-4 text-critical-red" />
                              <span className="text-sm font-medium text-critical-red">
                                {event.errorsCount}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary Box */}
        {syncEvents.length > 0 && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Sync Performance</h3>
            <div className="grid grid-cols-4 gap-4 text-sm text-blue-800">
              <div>
                <div className="text-2xl font-bold text-blue-900">
                  {syncEvents.filter(e => e.status === 'success').length}
                </div>
                <div className="text-xs">Successful syncs</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-900">
                  {syncEvents.reduce((sum, e) => sum + e.recordsAdded + e.recordsUpdated, 0)}
                </div>
                <div className="text-xs">Total records synced</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-900">
                  {Math.round(syncEvents.reduce((sum, e) => sum + e.duration, 0) / syncEvents.length / 1000)}s
                </div>
                <div className="text-xs">Average duration</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-900">
                  {syncEvents.filter(e => e.status === 'failed').length}
                </div>
                <div className="text-xs">Failed syncs</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
